import { useMemo, useState } from 'react';
import { ScrollableList } from 'components/DesignSystems/ScrollableList';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import type { ScopedPortalItem } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import { useUscgMapContext } from './UscgMapContext';
import classNames from 'classnames';

function selectionStats(items: ScopedPortalItem[], selectedIds: Set<string>) {
  const selectableItems = items.filter((item) => !!item.id);
  const selectedCount = selectableItems.reduce(
    (count, item) => count + (selectedIds.has(item.id!) ? 1 : 0),
    0
  );
  const all =
    selectableItems.length > 0 && selectedCount === selectableItems.length;
  const some = selectedCount > 0 && !all;

  return { selectedCount, all, some };
}

export const PortalItemRow: React.FC<{
  item: ScopedPortalItem;
  selectedItemIds: Set<string>;
  onToggleItem: (item: ScopedPortalItem, checked: boolean) => void;
}> = ({ item, selectedItemIds, onToggleItem }) => {
  const { previewLayers, setPreviewLayers } = useUscgMapContext();

  const isPreviewing =
    !!item.id && previewLayers.some((layer) => layer.id === item.id);
  const itemTitle = item.title || 'Untitled';

  return (
    <>
      <td className="py-1 ps-0">
        <StylishNewCheckbox
          checked={item.id ? selectedItemIds.has(item.id) : false}
          disabled={!item.id}
          title={`Select ${itemTitle}`}
          label={itemTitle}
          onClick={(event) => onToggleItem(item, event.currentTarget.checked)}
        />
      </td>
      <td className="py-1 ps-0">
        <StylishNewButton
          className={classNames(
            'button--icon border-0',
            isPreviewing ? 'bg-primary' : 'bg-bglight-hover'
          )}
          onClick={(event) => {
            event.stopPropagation();

            setPreviewLayers((prev) => {
              const next = [...prev];
              const found = next.findIndex((layer) => layer.id === item.id);

              if (found === -1) {
                next.push({
                  id: item.id,
                  url: item.url,
                  authType: item.authType,
                });
              } else {
                next.splice(found, 1);
              }

              return next;
            });
          }}
          title={`Preview ${itemTitle} on map`}
          aria-label={`Preview ${itemTitle} on map`}
        >
          <SharedIcon
            iconName={isPreviewing ? 'preview_off' : 'preview'}
            size="sm"
            aria-hidden
          />
        </StylishNewButton>
      </td>
    </>
  );
};

type SelectablePortalItemsListProps = {
  items: ScopedPortalItem[];
  onToggleAllItems: (items: ScopedPortalItem[], checked: boolean) => void;
  selectedItemIds: Set<string>;
  search?: string;
  onSearchChange?: (value: string) => void;
  children?: (filteredItems: ScopedPortalItem[]) => React.ReactNode;
  scrollableListStyle?: React.CSSProperties;
};

export const SelectablePortalItemsList: React.FC<
  SelectablePortalItemsListProps
> = ({
  items,
  onToggleAllItems,
  selectedItemIds,
  search,
  onSearchChange,
  children,
  scrollableListStyle,
}) => {
  const [internalSearch, setInternalSearch] = useState('');
  const searchValue = search ?? internalSearch;
  const setSearchValue = onSearchChange ?? setInternalSearch;

  const filteredItems = useMemo(() => {
    const searchTerm = searchValue.trim().toLowerCase();
    if (!searchTerm) {
      return items;
    }

    return items.filter((item) => {
      const title = item.title?.toLowerCase() ?? '';
      const type = item.type?.toLowerCase() ?? '';
      const description = item.description?.toLowerCase() ?? '';

      return (
        title.includes(searchTerm) ||
        type.includes(searchTerm) ||
        description.includes(searchTerm)
      );
    });
  }, [items, searchValue]);

  const allStats = useMemo(
    () => selectionStats(filteredItems, selectedItemIds),
    [filteredItems, selectedItemIds]
  );

  return (
    <div className="d-flex flex-column">
      <StylishNewSearchBar
        placeholder="Search items"
        value={searchValue}
        onChangeSearchTerm={setSearchValue}
        className="mb-4"
      />

      <label className="d-flex align-items-center user-select-none mb-3 gap-2">
        <StylishNewCheckbox
          checked={allStats.all}
          indeterminate={allStats.some}
          disabled={filteredItems.length === 0}
          onClick={(event) =>
            onToggleAllItems(filteredItems, event.currentTarget.checked)
          }
          title="Select all"
        />
        <span className="fw-semibold">Select all</span>
        <span className="text-muted">({filteredItems.length})</span>
      </label>

      <ScrollableList
        emptyMessage="No items found."
        viewportHeightPercentage={40}
        className="gap-2"
        style={scrollableListStyle}
      >
        {children?.(filteredItems)}
      </ScrollableList>
    </div>
  );
};
